import React from "react";
import clsx from "clsx";

export function Button({ variant = "primary", size = "md", className, children, ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-[background-color,transform,box-shadow,color] duration-150 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60";
  const variants = {
    primary: "bg-brand text-white hover:bg-[#e01f47] shadow-[0_2px_8px_-2px_rgba(254,44,85,0.45)]",
    cyan: "bg-cyan text-white hover:brightness-110",
    outline: "border border-line bg-white text-white hover:bg-white/10",
    ghost: "text-muted hover:text-white hover:bg-white/5",
    danger: "bg-white text-brand border border-brand/40 hover:bg-brand/10",
    success: "bg-success text-white hover:brightness-110",
  };
  const sizes = {
    sm: "text-xs px-3 py-1.5",
    md: "text-sm px-4 py-2.5",
    lg: "text-base px-6 py-3",
  };
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
}

export function Input({ className, label, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>}
      <input
        className={clsx(
          "w-full rounded-lg border border-line bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-muted/60 outline-none transition-colors duration-150 focus:border-brand",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Textarea({ className, label, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>}
      <textarea
        className={clsx(
          "w-full rounded-lg border border-line bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-muted/60 outline-none transition-colors duration-150 focus:border-brand",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Select({ className, label, children, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>}
      <select
        className={clsx(
          "w-full rounded-lg border border-line bg-black/40 px-3.5 py-2.5 text-sm text-white outline-none transition-colors duration-150 focus:border-brand",
          className
        )}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div
      className={clsx("rounded-xl border border-line bg-elevated shadow-card", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function Badge({ status, children }) {
  const map = {
    approved: "bg-success/15 text-success border-success/30",
    delivered: "bg-success/15 text-success border-success/30",
    paid: "bg-cyan/15 text-cyan border-cyan/30",
    shipped: "bg-cyan/15 text-cyan border-cyan/30",
    pending: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
    rejected: "bg-brand/15 text-brand border-brand/30",
    none: "bg-white/5 text-muted border-line",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize",
        map[children] || map[status] || "bg-white/5 text-muted border-line"
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }) {
  return (
    <div
      className={clsx(
        "h-6 w-6 animate-spin rounded-full border-2 border-line border-t-brand",
        className
      )}
    />
  );
}

export function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="modal-overlay"
    >
      <div
        className="w-full max-w-lg animate-fade-up rounded-2xl border border-line bg-elevated p-6 shadow-card-hover max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h3 className="mb-4 text-lg font-bold text-white">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

export function Stat({ label, value, accent = "brand", icon: Icon }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
          <div className="mt-2 font-head text-2xl font-bold text-white">{value}</div>
        </div>
        {Icon && (
          <div
            className={clsx(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              accent === "brand" && "bg-brand/15 text-brand",
              accent === "cyan" && "bg-cyan/15 text-cyan",
              accent === "success" && "bg-success/15 text-success"
            )}
          >
            <Icon size={20} />
          </div>
        )}
      </div>
    </Card>
  );
}
